/**
 * Created by Ning Jiang on 2022-06-10.
 */

import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/text/BitmapTextStyleOptions";
import {SerialGameAssets} from "../SerialGameAssets";
import {Serial} from "../Serial";
import {Layers, SerialLayers} from "./SerialLayers";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap, TimelineLite} from "gsap";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {XSplitSymbol} from "../symbol/XSplitSymbol";

export class Ways extends PIXI.Container {

    private readonly _landscapeStyle:BitmapTextStyleOptions = {
        fontName : SerialGameAssets.BITMAP_FONT_WAYS,
        fontSize : 36,
        tint : 0xffffff
    };

    private readonly _portraitStyle:BitmapTextStyleOptions = {
        fontName : SerialGameAssets.BITMAP_FONT_WAYS,
        fontSize : 30,
        tint : 0xffffff
    };

    private _landscapeContainer:PIXI.Container;
    private _landscapeNumber:PIXI.BitmapText;
    private _landscapeWays:PIXI.Sprite;
    private _landscapeSpine:PIXI.spine.Spine;
    private _portraitContainer:PIXI.Container;
    private _portraitNumber:PIXI.BitmapText;
    private _portraitWays:PIXI.Sprite;
    private _portraitSpine:PIXI.spine.Spine;
    private _animationTl:TimelineLite | null;

    private _currentLandscapeScale:number;
    private _currentPortraitScale:number;
    private _spineName:string;

    private _currentReelValue:number[][] = [
        [0, 0, 1, 1, 1, 0, 0],
        [0, 0, 1, 1, 1, 0, 0],
        [0, 0, 1, 1, 1, 0, 0],
        [0, 0, 1, 1, 1, 0, 0],
        [0, 0, 1, 1, 1, 0, 0],
    ];

    public get currentValue():number {
        return this._currentReelValue.reduce((a, b) => {
            return a * ArrayHelper.arraySum(b);
        }, 1);
    }

    constructor() {
        super();

        this._landscapeContainer = new PIXI.Container();
        this._landscapeContainer.position.set(-172, 190);
        this.addChild(this._landscapeContainer);

        this._landscapeNumber = new PIXI.BitmapText(this.currentValue.toString(), this._landscapeStyle);
        (<PIXI.Point>this._landscapeNumber.anchor).set(0.5, 1);
        this._landscapeNumber.letterSpacing = -10;
        this._landscapeContainer.addChild(this._landscapeNumber);

        this._landscapeWays = new PIXI.Sprite(Serial.assetLoader.getTextures(`totalways/waysWon`)[0]);
        this._landscapeWays.anchor.set(0.5, 0);
        this._landscapeWays.y = 30;
        this._landscapeContainer.addChild(this._landscapeWays);

        this._landscapeSpine = new PIXI.spine.Spine(Serial.assetLoader.getSpineAsset(`waysLandscape`));
        this._landscapeSpine.autoUpdate = true;
        this._landscapeSpine.position.set(172 + 360, -190 + 360);
        this._landscapeSpine.visible = false;
        this._landscapeContainer.addChild(this._landscapeSpine);

        this._portraitContainer = new PIXI.Container();
        this._portraitContainer.position.set(320, -84);
        this.addChild(this._portraitContainer);

        this._portraitNumber = new PIXI.BitmapText(this.currentValue.toString(), this._portraitStyle);
        (<PIXI.Point>this._portraitNumber.anchor).set(0, 0.5);
        this._portraitNumber.letterSpacing = -10;
        this._portraitContainer.addChild(this._portraitNumber);

        this._portraitWays = new PIXI.Sprite(Serial.assetLoader.getTextures(`totalways/waysWon`)[0]);
        this._portraitWays.anchor.set(1, 0.5);
        this._portraitWays.scale.set(0.9);
        this._portraitWays.y = 8;
        this._portraitContainer.addChild(this._portraitWays);

        this._portraitSpine = new PIXI.spine.Spine(Serial.assetLoader.getSpineAsset(`waysPortrait`));
        this._portraitSpine.autoUpdate = true;
        this._portraitSpine.position.set(-320 + 360, 84 + 360);
        this._portraitSpine.visible = false;
        this._portraitContainer.addChild(this._portraitSpine);

        SerialLayers.getLayer(Layers.BACKGROUND)?.addChild(this);

        this.updatePosition();
    }

    private updatePosition():void {
        const landscapeMaxWidth = 150;
        const portraitMaxWidth = 150;

        this._landscapeNumber.scale.set(1);
        this._portraitNumber.scale.set(1);
        this._landscapeNumber.updateTransform();
        this._portraitNumber.updateTransform();
        if(this._landscapeNumber.textWidth > landscapeMaxWidth) {
            this._landscapeNumber.scale.set(MathHelper.roundToDecimals(landscapeMaxWidth / this._landscapeNumber.textWidth, 2));
        }
        if(this._portraitNumber.textWidth > portraitMaxWidth) {
            this._portraitNumber.scale.set(MathHelper.roundToDecimals(portraitMaxWidth / this._portraitNumber.textWidth, 2));
        }
        this._currentLandscapeScale = this._landscapeNumber.scale.x;
        this._currentPortraitScale = this._portraitNumber.scale.x;

        const space = 10;
        const totalWidth = (this._portraitNumber.width + this._portraitWays.width + space);
        this._portraitNumber.x = -totalWidth * 0.5;
        this._portraitWays.x = totalWidth * 0.5;
    }

    public onOrientationChanged() {
        this._landscapeContainer.visible = NolimitApplication.isLandscape;
        this._portraitContainer.visible = !NolimitApplication.isLandscape;
    }

    public updateWays(reelId:number, symId:number, ways:number, playAni:boolean = true):void {
        const oldValue = this._currentReelValue[reelId][symId];
        if(oldValue != ways) {
            if(this._animationTl) {
                if(this._animationTl.isActive()) {
                    this._animationTl.progress(1);
                }
                this._animationTl.kill();
            }
            this._animationTl = null;

            this._currentReelValue[reelId][symId] = ways;
            const value = this.currentValue;
            this._landscapeNumber.text = this._portraitNumber.text = value.toString();
            this.updatePosition();
            if(!playAni) {
                return;
            }
            const tl = gsap.timeline();
            tl.add([
                gsap.to(this._landscapeNumber.scale, {
                    duration : 0.3,
                    x : this._currentLandscapeScale * 1.3,
                    y : this._currentLandscapeScale * 1.3
                }),
                // gsap.to(this._portraitNumber.scale, {
                //     duration : 0.3,
                //     x : this._currentPortraitScale * 1.3,
                //     y : this._currentPortraitScale * 1.3
                // }),
                gsap.to(this._landscapeNumber, {duration : 0.3, pixi : {brightness : 2}}),
                gsap.to(this._portraitNumber, {duration : 0.3, pixi : {brightness : 2}}),
                gsap.to(this._landscapeWays, {duration : 0.3, pixi : {brightness : 2}}),
                gsap.to(this._portraitWays, {duration : 0.3, pixi : {brightness : 2}})
            ]);
            tl.add([
                gsap.to(this._landscapeNumber.scale, {
                    duration : 0.5,
                    x : this._currentLandscapeScale,
                    y : this._currentLandscapeScale
                }),
                // gsap.to(this._portraitNumber.scale, {
                //     duration : 0.5,
                //     x : this._currentPortraitScale,
                //     y : this._currentPortraitScale
                // }),
                gsap.to(this._landscapeNumber, {duration : 0.5, pixi : {brightness : 1}}),
                gsap.to(this._portraitNumber, {duration : 0.5, pixi : {brightness : 1}}),
                gsap.to(this._landscapeWays, {duration : 0.5, pixi : {brightness : 1}}),
                gsap.to(this._portraitWays, {duration : 0.5, pixi : {brightness : 1}})
            ]);
            if(this._landscapeContainer.visible) {
                const landscapeTl = gsap.timeline();
                landscapeTl.add(() => {
                    this._landscapeSpine.visible = true;
                    this._landscapeSpine.state.setAnimation(0, this._spineName, false);
                });
                landscapeTl.add(() => {
                    this._landscapeSpine.state.clearTrack(0);
                    this._landscapeSpine.skeleton.setToSetupPose();
                    this._landscapeSpine.state.setEmptyAnimation(0, 0);
                    this._landscapeSpine.visible = false;
                }, this._landscapeSpine.spineData.findAnimation(this._spineName).duration);
                tl.add(landscapeTl, 0);
            }
            if(this._portraitContainer.visible) {
                const portraitTl = gsap.timeline();
                portraitTl.add(() => {
                    this._portraitSpine.visible = true;
                    this._portraitSpine.state.setAnimation(0, this._spineName, false);
                });
                portraitTl.add(() => {
                    this._portraitSpine.state.clearTrack(0);
                    this._portraitSpine.skeleton.setToSetupPose();
                    this._portraitSpine.state.setEmptyAnimation(0, 0);
                    this._portraitSpine.visible = false;
                }, this._portraitSpine.spineData.findAnimation(this._spineName).duration);
                tl.add(portraitTl, 0);
            }
        }
    }

    public resetReel(reelId:number):void {
        [2, 3, 4].forEach(symId => {
            if(reelId < 4) {
                this.updateWays(reelId, symId, 1);
                return;
            }
            if(Serial.model.fullReels[reelId][symId].includes("S2") && !Serial.model.nextSpinPositions[reelId][symId]) {
                this.updateWays(reelId, symId, (<XSplitSymbol>Serial.getSymbol(reelId, symId)).attachedValue);
                return;
            }
            this.updateWays(reelId, symId, 1);
        });
    }

    public setMode(mode:string):void {
        if(mode.includes("SUPER")) {
            this._landscapeNumber.tint = 0xd30f0f;
            this._landscapeWays.tint = 0xd30f0f;
            this._portraitNumber.tint = 0xb31e19;
            this._portraitWays.tint = 0xb31e19;
            this._spineName = "freespinSuper";
        } else if(mode.includes("FREESPIN")) {
            this._landscapeNumber.tint = 0x69497a;
            this._landscapeWays.tint = 0x69497a;
            this._portraitNumber.tint = 0xcb23b1;
            this._portraitWays.tint = 0xcb23b1;
            this._spineName = "freespin";
        } else {
            this._landscapeNumber.tint = 0x415053;
            this._landscapeWays.tint = 0x415053;
            this._portraitNumber.tint = 0xe24806;
            this._portraitWays.tint = 0xe24806;
            this._spineName = "normal";
        }
    }
}